import React, { useState, useContext, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";

import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import TextField from "@material-ui/core/TextField";
import Link from "@material-ui/core/Link";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { makeStyles } from "@material-ui/core/styles";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import EmailIcon from "@material-ui/icons/Email";
import LockIcon from "@material-ui/icons/Lock";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import ChatRoundedIcon from "@material-ui/icons/ChatRounded";
import FlashOnRoundedIcon from "@material-ui/icons/FlashOnRounded";
import BarChartRoundedIcon from "@material-ui/icons/BarChartRounded";
import VerifiedUserRoundedIcon from "@material-ui/icons/VerifiedUserRounded";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import useSettings from "../../hooks/useSettings";
import isNativeApp from "../../utils/isNativeApp";

// Roboto across the board — Raviv 2026-05-19 standardisation: keeps
// the Login page typographically consistent with the rest of the CRM
// instead of a Fraunces/Manrope island. Constants retained so we
// don't touch every consumer site in the file.
const FONT_DISPLAY = `'Roboto',system-ui,-apple-system,'Segoe UI','Helvetica Neue',sans-serif`;
const FONT_BODY    = `'Roboto',system-ui,-apple-system,'Segoe UI','Helvetica Neue',sans-serif`;

const useStyles = makeStyles((theme) => {
	const isRTL = theme.direction === "rtl";
	const isDark = theme.palette.type === "dark";

	return {
		// ============================================================
		// PAGE SHELL
		// ============================================================
		root: {
			width: "100%",
			minHeight: "100dvh",
			fallbacks: [{ minHeight: "100vh" }], // older WebViews without dvh
			display: "grid",
			// Light card on the right has slightly more visual weight
			// because of the form chrome; balance with a touch more
			// space on the dark side. RTL: grid auto-flips so the
			// brand pane anchors to the reader's start direction.
			gridTemplateColumns: "1.05fr 1fr",
			fontFamily: FONT_BODY,
			background: isDark ? "#0a0f0d" : "#ffffff",
			[theme.breakpoints.down("sm")]: {
				gridTemplateColumns: "1fr",
			},
		},

		// ============================================================
		// BRAND PANE — dark, layered, lit
		// ============================================================
		brand: {
			position: "relative",
			overflow: "hidden",
			padding: "56px 64px 48px",
			display: "flex",
			flexDirection: "column",
			gap: 32,
			color: "#e8f4ec",
			// Deep moss → near-black gradient with two radial lights.
			// One brand-green glow upper-inline-start, one teal lower-
			// inline-end. Reads as deliberate lighting, not a flat tint.
			background: `
				radial-gradient(900px 700px at 18% 16%, rgba(46,227,122,0.18), transparent 62%),
				radial-gradient(700px 600px at 88% 88%, rgba(18,140,126,0.20), transparent 62%),
				linear-gradient(155deg, #0a1812 0%, #050b08 60%, #02060a 100%)
			`,
			[theme.breakpoints.down("md")]: {
				padding: "44px 48px 40px",
			},
			[theme.breakpoints.down("sm")]: {
				display: "none",
			},
		},

		// Faint concentric rings around the chat illustration —
		// pure CSS, sits behind everything else. Brand-green at
		// extremely low opacity so it reads as ambient atmosphere.
		brandRings: {
			position: "absolute",
			top: "32%",
			[isRTL ? "left" : "right"]: -120,
			width: 640,
			height: 640,
			borderRadius: "50%",
			border: "1px solid rgba(46,227,122,0.06)",
			pointerEvents: "none",
			"&::before, &::after": {
				content: '""',
				position: "absolute",
				borderRadius: "50%",
				border: "1px solid rgba(46,227,122,0.05)",
			},
			"&::before": { inset: 50 },
			"&::after": { inset: 110 },
		},
		// Tiny dotted texture across the pane (28px grid) — same
		// recipe as the rest of the brand surfaces.
		brandDots: {
			position: "absolute",
			inset: 0,
			backgroundImage:
				"radial-gradient(rgba(232,244,236,0.05) 1px, transparent 1px)",
			backgroundSize: "28px 28px",
			pointerEvents: "none",
		},

		// ── Brand header (logo + wordmark + eyebrow) ─────────────
		brandHeader: {
			position: "relative",
			display: "flex",
			alignItems: "center",
			gap: 14,
			zIndex: 2,
			animation: "$fadeUp 0.7s cubic-bezier(0.2,0.8,0.2,1) both",
		},
		logoChip: {
			width: 56,
			height: 56,
			borderRadius: 16,
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			background: "linear-gradient(135deg, #2ee37a 0%, #128c7e 100%)",
			color: "#ffffff",
			boxShadow:
				"0 0 0 1px rgba(46,227,122,0.32), 0 14px 28px rgba(46,227,122,0.32), inset 0 1px 0 rgba(255,255,255,0.22)",
			flexShrink: 0,
			"& svg": { fontSize: 30 },
		},
		brandWord: {
			display: "flex",
			flexDirection: "column",
			gap: 2,
		},
		brandWordMark: {
			fontFamily: FONT_DISPLAY,
			fontWeight: 700,
			fontSize: 44,
			lineHeight: 1,
			letterSpacing: "-0.022em",
			color: "#ffffff",
			fontVariationSettings: '"opsz" 72, "SOFT" 30',
		},
		brandWordEyebrow: {
			fontFamily: FONT_BODY,
			fontWeight: 700,
			fontSize: 11,
			letterSpacing: "0.32em",
			textTransform: "uppercase",
			color: "#2ee37a",
			marginTop: 2,
		},

		// ── Hero block (headline + accent + subhead) ─────────────
		hero: {
			position: "relative",
			maxWidth: 560,
			zIndex: 2,
			marginTop: 8,
			animation: "$fadeUp 0.85s 0.1s cubic-bezier(0.2,0.8,0.2,1) both",
		},
		headline: {
			fontFamily: FONT_DISPLAY,
			fontWeight: 600,
			fontSize: "clamp(48px, 5.6vw, 76px)",
			lineHeight: 1.04,
			letterSpacing: "-0.022em",
			color: "#ffffff",
			margin: 0,
			fontVariationSettings: '"opsz" 144, "SOFT" 30',
			"& em": {
				fontStyle: "normal",
				color: "#2ee37a",
				// Subtle text-shadow glow on the accent so it reads as
				// "lit", matching the page's lighting concept.
				textShadow: "0 0 32px rgba(46,227,122,0.45)",
			},
		},
		accentRule: {
			width: 56,
			height: 2,
			background: "linear-gradient(90deg, #2ee37a 0%, transparent 100%)",
			marginTop: 28,
			marginBottom: 22,
			borderRadius: 2,
			animation: "$lineGrow 0.9s 0.4s cubic-bezier(0.2,0.8,0.2,1) both",
			transformOrigin: isRTL ? "right center" : "left center",
		},
		subhead: {
			fontFamily: FONT_BODY,
			fontWeight: 400,
			fontSize: "clamp(15px, 1.15vw, 17px)",
			lineHeight: 1.55,
			color: "rgba(232,244,236,0.78)",
			margin: 0,
			maxWidth: "44ch",
		},

		// ── Chat illustration (CSS-only mockup) ──────────────────
		chatStage: {
			position: "absolute",
			top: "20%",
			[isRTL ? "left" : "right"]: 80,
			width: 360,
			height: 360,
			zIndex: 1,
			pointerEvents: "none",
			[theme.breakpoints.down("md")]: {
				width: 280,
				height: 280,
				[isRTL ? "left" : "right"]: 32,
			},
			animation: "$fadeUp 1s 0.5s cubic-bezier(0.2,0.8,0.2,1) both",
		},
		// Frosted glass plate behind everything else
		chatPlate: {
			position: "absolute",
			inset: 24,
			borderRadius: 28,
			background:
				"linear-gradient(160deg, rgba(46,227,122,0.10) 0%, rgba(18,140,126,0.06) 100%)",
			backdropFilter: "blur(12px)",
			WebkitBackdropFilter: "blur(12px)",
			border: "1px solid rgba(46,227,122,0.18)",
			boxShadow:
				"inset 0 1px 0 rgba(255,255,255,0.06), 0 30px 60px rgba(0,0,0,0.45)",
		},
		// WhatsApp glyph at center — the anchor of the illustration
		chatWhats: {
			position: "absolute",
			top: "52%",
			[isRTL ? "right" : "left"]: "44%",
			width: 72,
			height: 72,
			borderRadius: "50%",
			background: "linear-gradient(135deg, #25d366 0%, #128c7e 100%)",
			color: "#ffffff",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			boxShadow:
				"0 0 0 1px rgba(46,227,122,0.45), 0 18px 36px rgba(46,227,122,0.32)",
			animation: "$chatBob 4.6s ease-in-out infinite",
			"& svg": { fontSize: 38 },
		},
		// Three speech bubbles drifting in around the glyph. Each
		// bubble is just a rounded rect with a tiny tail; sizes and
		// positions are different so the cluster reads as varied
		// conversation, not a tile.
		chatBubble: {
			position: "absolute",
			borderRadius: 14,
			background: "rgba(255,255,255,0.06)",
			border: "1px solid rgba(255,255,255,0.10)",
			boxShadow: "0 14px 32px rgba(0,0,0,0.32)",
			backdropFilter: "blur(6px)",
			WebkitBackdropFilter: "blur(6px)",
			// "Faux content" lines — keeps the bubble from reading
			// as a blank shape.
			"&::before, &::after": {
				content: '""',
				position: "absolute",
				borderRadius: 4,
				background: "rgba(255,255,255,0.20)",
			},
		},
		bubbleA: {
			width: 168,
			height: 56,
			top: 32,
			[isRTL ? "right" : "left"]: 56,
			animation: "$bubbleFloatA 5s ease-in-out infinite",
			"&::before": { top: 14, left: 14, width: 96, height: 8 },
			"&::after": { top: 30, left: 14, width: 64, height: 8 },
		},
		bubbleB: {
			width: 140,
			height: 48,
			top: 120,
			[isRTL ? "left" : "right"]: 40,
			background: "rgba(46,227,122,0.18)",
			border: "1px solid rgba(46,227,122,0.30)",
			animation: "$bubbleFloatB 5.6s ease-in-out infinite 0.4s",
			"&::before": { top: 12, left: 12, width: 80, height: 8, background: "rgba(232,244,236,0.62)" },
			"&::after": { top: 26, left: 12, width: 56, height: 8, background: "rgba(232,244,236,0.42)" },
		},
		bubbleC: {
			width: 200,
			height: 64,
			bottom: 28,
			[isRTL ? "right" : "left"]: 36,
			animation: "$bubbleFloatA 6.2s ease-in-out infinite 0.8s",
			"&::before": { top: 14, left: 14, width: 116, height: 8 },
			"&::after": { top: 30, left: 14, width: 80, height: 8 },
		},

		// ── Feature pills (3 of them, icon + 2-line text) ────────
		features: {
			position: "relative",
			marginTop: "auto",
			display: "grid",
			gridTemplateColumns: "1fr 1fr 1fr",
			gap: 28,
			zIndex: 2,
			animation: "$fadeUp 0.9s 0.7s cubic-bezier(0.2,0.8,0.2,1) both",
			[theme.breakpoints.down("md")]: {
				gridTemplateColumns: "1fr",
				gap: 20,
			},
		},
		feature: {
			display: "flex",
			flexDirection: "column",
			gap: 12,
		},
		featureIcon: {
			width: 48,
			height: 48,
			borderRadius: 14,
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			background:
				"linear-gradient(135deg, rgba(46,227,122,0.20) 0%, rgba(18,140,126,0.12) 100%)",
			border: "1px solid rgba(46,227,122,0.28)",
			color: "#2ee37a",
			boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06)",
			"& svg": { fontSize: 22 },
		},
		featureTitle: {
			fontFamily: FONT_DISPLAY,
			fontWeight: 600,
			fontSize: 17,
			letterSpacing: "-0.012em",
			lineHeight: 1.18,
			color: "#ffffff",
			margin: 0,
			fontVariationSettings: '"opsz" 24, "SOFT" 40',
		},
		featureBody: {
			fontFamily: FONT_BODY,
			fontSize: 12.5,
			fontWeight: 400,
			lineHeight: 1.5,
			color: "rgba(232,244,236,0.62)",
			margin: 0,
		},

		// ============================================================
		// FORM PANE — clean, glass card on white
		// ============================================================
		formPane: {
			position: "relative",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			padding: "40px",
			background: isDark ? "#0a0f0d" : "#ffffff",
			[theme.breakpoints.down("sm")]: {
				padding: "32px 20px",
				minHeight: "100dvh",
				fallbacks: [{ minHeight: "100vh" }], // older WebViews without dvh
				background: isDark
					? "radial-gradient(900px 720px at 50% 0%, rgba(37,211,102,0.06), transparent 60%), #0a0f0d"
					: "radial-gradient(900px 720px at 50% 0%, rgba(37,211,102,0.10), transparent 60%), #ffffff",
			},
		},
		formCard: {
			position: "relative",
			width: "100%",
			maxWidth: 440,
			padding: "48px 44px 36px",
			borderRadius: 22,
			background: isDark ? "#131614" : "#ffffff",
			border: isDark
				? "1px solid rgba(255,255,255,0.08)"
				: "1px solid rgba(15,22,20,0.06)",
			boxShadow: isDark
				? "0 30px 60px rgba(0,0,0,0.45), 0 4px 12px rgba(0,0,0,0.35)"
				: "0 30px 60px rgba(15,22,20,0.06), 0 4px 12px rgba(15,22,20,0.04)",
			animation: "$cardIn 0.8s 0.15s cubic-bezier(0.2,0.8,0.2,1) both",
			[theme.breakpoints.down("xs")]: {
				padding: "36px 24px",
				borderRadius: 18,
			},
		},

		// Mobile-only brand row above the card (sm and below the
		// dark side disappears entirely).
		mobileBrand: {
			display: "none",
			[theme.breakpoints.down("sm")]: {
				display: "flex",
				alignItems: "center",
				gap: 10,
				marginBottom: 28,
				justifyContent: "center",
			},
		},
		mobileBrandText: {
			fontFamily: FONT_DISPLAY,
			fontWeight: 700,
			fontSize: 22,
			letterSpacing: "-0.018em",
			color: isDark ? "#f2f5f3" : "#0a2620",
		},

		welcomeText: {
			fontFamily: FONT_DISPLAY,
			fontWeight: 600,
			fontSize: 32,
			letterSpacing: "-0.024em",
			lineHeight: 1.1,
			color: isDark ? "#f2f5f3" : "#0a1812",
			margin: 0,
			textAlign: "center",
			fontVariationSettings: '"opsz" 36, "SOFT" 30',
		},
		subtitleText: {
			fontFamily: FONT_BODY,
			fontSize: 13.5,
			fontWeight: 400,
			lineHeight: 1.5,
			color: isDark ? "rgba(255,255,255,0.62)" : "rgba(15,22,20,0.55)",
			margin: 0,
			marginTop: 8,
			textAlign: "center",
		},

		form: {
			width: "100%",
			marginTop: 32,
		},
		fieldLabel: {
			fontFamily: FONT_BODY,
			fontSize: 13,
			fontWeight: 600,
			color: isDark ? "rgba(255,255,255,0.72)" : "rgba(15,22,20,0.7)",
			marginBottom: 8,
			display: "block",
		},
		textField: {
			marginBottom: 16,
			"& .MuiOutlinedInput-root": {
				borderRadius: 12,
				backgroundColor: isDark ? "#1b1f1d" : "#fbfdfb",
				fontFamily: FONT_BODY,
				fontSize: 14.5,
				height: 50,
				transition: "box-shadow 0.18s ease",
				"& fieldset": {
					borderColor: isDark ? "rgba(255,255,255,0.14)" : "rgba(15,22,20,0.10)",
					transition: "border-color 0.18s ease, border-width 0.18s ease",
				},
				"&:hover fieldset": {
					borderColor: isDark ? "rgba(255,255,255,0.28)" : "rgba(15,22,20,0.22)",
				},
				"&.Mui-focused fieldset": {
					borderColor: "#128c7e",
					borderWidth: 1.5,
				},
				"&.Mui-focused": {
					boxShadow: "0 0 0 4px rgba(18,140,126,0.14)",
				},
			},
			"& .MuiOutlinedInput-input": {
				padding: "14px 14px",
				fontSize: 16,
				color: isDark ? "#f2f5f3" : "#0a1812",
				fontFamily: FONT_BODY,
			},
			// Mask Chrome autofill — overrides any future inline white
			// caller styles (we removed them but keep the guard).
			"& .MuiInputBase-input:-webkit-autofill, & .MuiInputBase-input:-webkit-autofill:hover, & .MuiInputBase-input:-webkit-autofill:focus, & .MuiInputBase-input:-webkit-autofill:active":
				{
					WebkitTextFillColor: isDark ? "#f2f5f3" : "#0a1812",
					caretColor: isDark ? "#f2f5f3" : "#0a1812",
					WebkitBoxShadow: isDark ? "0 0 0 1000px #1b1f1d inset" : "0 0 0 1000px #fbfdfb inset",
					transition: "background-color 50000s ease-in-out 0s",
					fontFamily: FONT_BODY,
				},
			"& .MuiInputAdornment-root .MuiSvgIcon-root": {
				color: isDark ? "rgba(255,255,255,0.5)" : "rgba(15,22,20,0.45)",
				fontSize: 20,
			},
		},

		// ── Remember me + Forgot password row ────────────────────
		extrasRow: {
			display: "flex",
			alignItems: "center",
			justifyContent: "space-between",
			marginTop: 4,
			marginBottom: 22,
			gap: 12,
			flexWrap: "wrap",
		},
		rememberLabel: {
			margin: 0,
			"& .MuiFormControlLabel-label": {
				fontFamily: FONT_BODY,
				fontSize: 13.5,
				fontWeight: 500,
				color: isDark ? "rgba(255,255,255,0.75)" : "rgba(15,22,20,0.72)",
			},
		},
		rememberBox: {
			padding: 6,
			color: isDark ? "rgba(255,255,255,0.4)" : "rgba(15,22,20,0.32)",
			"&.Mui-checked": {
				color: "#128c7e",
			},
		},
		forgotLink: {
			fontFamily: FONT_BODY,
			fontSize: 13.5,
			fontWeight: 600,
			color: "#128c7e",
			textDecoration: "none",
			cursor: "pointer",
			borderBottom: "1px solid transparent",
			transition: "border-color 0.18s ease",
			"&:hover": {
				borderBottomColor: "#128c7e",
				textDecoration: "none",
			},
		},

		submit: {
			width: "100%",
			height: 52,
			borderRadius: 12,
			fontFamily: FONT_BODY,
			fontWeight: 700,
			fontSize: 15,
			letterSpacing: "0.01em",
			textTransform: "none",
			color: "#ffffff",
			background:
				"linear-gradient(135deg, #2ee37a 0%, #25d366 45%, #128c7e 100%)",
			boxShadow:
				"0 16px 32px rgba(18,140,126,0.30), inset 0 1px 0 rgba(255,255,255,0.28)",
			transition:
				"transform 0.18s ease, filter 0.18s ease, box-shadow 0.18s ease",
			"&:hover": {
				background:
					"linear-gradient(135deg, #2ee37a 0%, #25d366 45%, #128c7e 100%)",
				transform: "translateY(-1px)",
				filter: "brightness(1.04)",
				boxShadow:
					"0 20px 36px rgba(18,140,126,0.34), inset 0 1px 0 rgba(255,255,255,0.28)",
			},
			"&:active": {
				transform: "translateY(0)",
				filter: "brightness(0.98)",
			},
		},

		// ── Optional sign-up link (gated on allowSignup) ─────────
		signupRow: {
			marginTop: 22,
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			gap: 6,
			fontFamily: FONT_BODY,
			fontSize: 13.5,
			color: isDark ? "rgba(255,255,255,0.62)" : "rgba(15,22,20,0.55)",
		},
		signupLink: {
			fontFamily: FONT_BODY,
			fontWeight: 700,
			color: "#128c7e",
			textDecoration: "none",
			"&:hover": { textDecoration: "underline" },
		},

		// ── Secure & encrypted footer ────────────────────────────
		secureFooter: {
			marginTop: 36,
			display: "flex",
			alignItems: "center",
			gap: 12,
			padding: "0 4px",
		},
		secureIconWrap: {
			width: 38,
			height: 38,
			borderRadius: 12,
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			background:
				"linear-gradient(135deg, rgba(46,227,122,0.16), rgba(18,140,126,0.12))",
			border: "1px solid rgba(18,140,126,0.22)",
			color: "#128c7e",
			"& svg": { fontSize: 20 },
		},
		secureText: {
			display: "flex",
			flexDirection: "column",
			gap: 1,
		},
		secureTitle: {
			fontFamily: FONT_BODY,
			fontSize: 12.5,
			fontWeight: 700,
			color: isDark ? "#e8f4ec" : "#0a2620",
		},
		secureBody: {
			fontFamily: FONT_BODY,
			fontSize: 11.5,
			fontWeight: 500,
			color: isDark ? "rgba(255,255,255,0.6)" : "rgba(15,22,20,0.50)",
		},

		// ============================================================
		// KEYFRAMES
		// ============================================================
		"@keyframes fadeUp": {
			from: { opacity: 0, transform: "translateY(14px)" },
			to:   { opacity: 1, transform: "translateY(0)" },
		},
		"@keyframes lineGrow": {
			from: { transform: "scaleX(0)" },
			to:   { transform: "scaleX(1)" },
		},
		"@keyframes cardIn": {
			from: { opacity: 0, transform: "translateY(10px) scale(0.985)" },
			to:   { opacity: 1, transform: "translateY(0) scale(1)" },
		},
		"@keyframes chatBob": {
			"0%, 100%": { transform: "translateY(0)" },
			"50%":      { transform: "translateY(-6px)" },
		},
		"@keyframes bubbleFloatA": {
			"0%, 100%": { transform: "translate(0, 0)" },
			"50%":      { transform: "translate(4px, -4px)" },
		},
		"@keyframes bubbleFloatB": {
			"0%, 100%": { transform: "translate(0, 0)" },
			"50%":      { transform: "translate(-4px, 4px)" },
		},
	};
});

const Login = () => {
	const classes = useStyles();
	const { getPublicSetting } = useSettings();

	const [user, setUser] = useState({ email: "", password: "" });
	const [allowSignup, setAllowSignup] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [remember, setRemember] = useState(true);
	const [loading, setLoading] = useState(false);

	const { handleLogin } = useContext(AuthContext);

	useEffect(() => {
		getPublicSetting("allowSignup")
			.then((data) => setAllowSignup(data === "enabled"))
			.catch((err) => console.log("Error reading setting", err));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleChangeInput = (e) => {
		setUser({ ...user, [e.target.name]: e.target.value });
	};

	const handlSubmit = async (e) => {
		e.preventDefault();
		if (loading) return;
		setLoading(true);
		try {
			await handleLogin(user);
		} finally {
			setLoading(false);
		}
	};

	const handleTogglePassword = () => setShowPassword(!showPassword);

	const features = [
		{
			icon: <ChatRoundedIcon />,
			title: i18n.t("login.brand.f1.t", {
				defaultValue: "All conversations in one place",
			}),
			body: i18n.t("login.brand.f1.b", {
				defaultValue: "Never miss a message. Stay organized.",
			}),
		},
		{
			icon: <FlashOnRoundedIcon />,
			title: i18n.t("login.brand.f2.t", {
				defaultValue: "Automate and save time",
			}),
			body: i18n.t("login.brand.f2.b", {
				defaultValue: "Smart automation for faster results.",
			}),
		},
		{
			icon: <BarChartRoundedIcon />,
			title: i18n.t("login.brand.f3.t", {
				defaultValue: "Insights that drive growth",
			}),
			body: i18n.t("login.brand.f3.b", {
				defaultValue: "Know more, decide better, close more.",
			}),
		},
	];

	return (
		<div className={classes.root}>
			{/* ===== BRAND PANE (md+ only) ===== */}
			<aside className={classes.brand}>
				<div className={classes.brandRings} aria-hidden="true" />
				<div className={classes.brandDots} aria-hidden="true" />

				<div className={classes.brandHeader}>
					<span className={classes.logoChip}>
						<ChatRoundedIcon />
					</span>
					<div className={classes.brandWord}>
						<span className={classes.brandWordMark}>Whapiz</span>
						<span className={classes.brandWordEyebrow}>
							{i18n.t("login.brand.meta", { defaultValue: "WhatsApp CRM" })}
						</span>
					</div>
				</div>

				<div className={classes.hero}>
					<h1 className={classes.headline}>
						{i18n.t("login.brand.h1", { defaultValue: "Conversations" })}
						<br />
						{i18n.t("login.brand.h2a", { defaultValue: "that " })}
						<em>{i18n.t("login.brand.h2b", { defaultValue: "close" })}</em>{" "}
						{i18n.t("login.brand.h3", { defaultValue: "deals." })}
					</h1>
					<div className={classes.accentRule} aria-hidden="true" />
					<p className={classes.subhead}>
						{i18n.t("login.brand.subhead", {
							defaultValue:
								"AI-powered WhatsApp CRM that helps you manage, automate and close more deals — all in one place.",
						})}
					</p>
				</div>

				{/* Floating chat illustration — CSS-only. WhatsApp glyph at
				    centre, three speech bubbles drifting in around it. No
				    network requests, no asset dependency. */}
				<div className={classes.chatStage} aria-hidden="true">
					<div className={classes.chatPlate} />
					<div className={`${classes.chatBubble} ${classes.bubbleA}`} />
					<div className={`${classes.chatBubble} ${classes.bubbleB}`} />
					<div className={`${classes.chatBubble} ${classes.bubbleC}`} />
					<div className={classes.chatWhats}>
						<ChatRoundedIcon />
					</div>
				</div>

				<div className={classes.features}>
					{features.map((f, i) => (
						<div key={i} className={classes.feature}>
							<span className={classes.featureIcon}>{f.icon}</span>
							<h3 className={classes.featureTitle}>{f.title}</h3>
							<p className={classes.featureBody}>{f.body}</p>
						</div>
					))}
				</div>
			</aside>

			{/* ===== FORM PANE ===== */}
			<main className={classes.formPane}>
				<div className={classes.formCard}>
					{/* Mobile-only brand row (dark brand pane is hidden on sm and below) */}
					<div className={classes.mobileBrand}>
						<span className={classes.logoChip}>
							<ChatRoundedIcon />
						</span>
						<span className={classes.mobileBrandText}>Whapiz</span>
					</div>

					<h2 className={classes.welcomeText}>
						{i18n.t("login.title", { defaultValue: "Welcome back" })}
					</h2>
					<p className={classes.subtitleText}>
						{i18n.t("login.subtitle", {
							defaultValue: "Sign in to continue to your workspace",
						})}
					</p>

					<form className={classes.form} noValidate onSubmit={handlSubmit}>
						<label htmlFor="email" className={classes.fieldLabel}>
							{i18n.t("login.form.email")}
						</label>
						<TextField
							variant="outlined"
							required
							fullWidth
							type="email"
							id="email"
							placeholder="you@company.com"
							name="email"
							value={user.email}
							onChange={handleChangeInput}
							autoComplete="email"
							autoFocus
							className={classes.textField}
							inputProps={{
								inputMode: "email",
								autoCapitalize: "none",
								autoCorrect: "off",
								spellCheck: "false",
							}}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<EmailIcon />
									</InputAdornment>
								),
							}}
						/>

						<label htmlFor="password" className={classes.fieldLabel}>
							{i18n.t("login.form.password")}
						</label>
						<TextField
							variant="outlined"
							required
							fullWidth
							name="password"
							placeholder="••••••••"
							type={showPassword ? "text" : "password"}
							id="password"
							value={user.password}
							onChange={handleChangeInput}
							autoComplete="current-password"
							className={classes.textField}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<LockIcon />
									</InputAdornment>
								),
								endAdornment: (
									<InputAdornment position="end">
										<IconButton
											aria-label="toggle password visibility"
											onClick={handleTogglePassword}
											edge="end"
										>
											{showPassword ? <VisibilityOff /> : <Visibility />}
										</IconButton>
									</InputAdornment>
								),
							}}
						/>

						{/* Remember me + Forgot password row. /forgot-password
						    is wired now (email-magic-link flow via Resend) — swapped
						    the placeholder span for a RouterLink. */}
						<div className={classes.extrasRow}>
							<FormControlLabel
								className={classes.rememberLabel}
								control={
									<Checkbox
										className={classes.rememberBox}
										checked={remember}
										onChange={(e) => setRemember(e.target.checked)}
										size="small"
									/>
								}
								label={i18n.t("login.form.remember", {
									defaultValue: "Remember me",
								})}
							/>
							<RouterLink to="/forgot-password" className={classes.forgotLink} style={{ textDecoration: "none" }}>
								{i18n.t("login.form.forgot", {
									defaultValue: "Forgot password?",
								})}
							</RouterLink>
						</div>

						<Button
							type="submit"
							fullWidth
							variant="contained"
							className={classes.submit}
							disableElevation
							disabled={loading}
						>
							{loading ? (
								<CircularProgress size={22} style={{ color: "#ffffff" }} />
							) : (
								i18n.t("login.buttons.submit")
							)}
						</Button>

						{/* Apple App Store Guideline 3.1.3(c): the native app is B2B
							login-only — accounts are provisioned by the organization on
							the web. Never show a self-signup link inside the app, even
							if the allowSignup setting is later enabled for the website
							(also: /signup is not a registered route in the app shell,
							so the link would land on a 404 — a 2.1 rejection risk). */}
						{allowSignup && !isNativeApp() && (
							<div className={classes.signupRow}>
								<span>
									{i18n.t("login.buttons.haveAccount", {
										defaultValue: "New here?",
									})}
								</span>
								<Link
									component={RouterLink}
									to="/signup"
									className={classes.signupLink}
								>
									{i18n.t("login.buttons.register")}
								</Link>
							</div>
						)}
					</form>

					{/* Secure & encrypted assurance — shield icon pill on
					    the leading edge, two-line copy. Matches the
					    reference layout, replaces the previous "ENCRYPTED ·
					    SINGLE SIGN-ON READY" all-caps footnote. */}
					<div className={classes.secureFooter}>
						<span className={classes.secureIconWrap}>
							<VerifiedUserRoundedIcon />
						</span>
						<div className={classes.secureText}>
							<span className={classes.secureTitle}>
								{i18n.t("login.secure.title", {
									defaultValue: "Secure & encrypted",
								})}
							</span>
							<span className={classes.secureBody}>
								{i18n.t("login.secure.body", {
									defaultValue: "Your data is safe with us",
								})}
							</span>
						</div>
					</div>
				</div>
			</main>
		</div>
	);
};

export default Login;
